import React from "react";
import { Box, Grid, Typography } from "@mui/material";
// ✅ Import the dashboard image from assets
import dashboardGraphic from "../../../../assets/home-dashboard-graphic.png";

export default function HowItWorks() {
  const steps = [
    {
      number: "1",
      label: "We Send Privacy Requests For You",
      description:
        "We contact data brokers on your behalf and request removal of your exposed personal information.",
    },
    {
      number: "2",
      label: "We Track Every Deletion Request",
      description:
        "You get real-time updates as each request moves through the removal process – from sent to deleted.",
    },
    {
      number: "3",
      label: "We Show You What's Been Deleted",
      description:
        "Once your data is removed, your dashboard updates instantly so you always know what's been cleaned.",
    },
  ];

  return (
    <Box component="section" sx={{ width: "100%", backgroundColor: "white" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ textAlign: "left", mb: { xs: 3, sm: 5 } }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 900,
              lineHeight: 1.25,
              color: "black",
            }}
          >
            How We Keep Your Information{" "}
            <Box component="span" sx={{ color: "#12b2f1" }}>
              Secure
            </Box>
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          {/* ... SVG Trails (unchanged) ... */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "hidden",
              zIndex: 0,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 1100 500"
              preserveAspectRatio="xMidYMid slice"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M 0 120 Q 180 60 320 180 Q 460 300 600 200 Q 740 100 900 220 Q 1000 290 1100 240"
                stroke="rgba(18,178,241,0.55)"
                strokeWidth="2"
                strokeDasharray="5 10"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M 0 300 Q 150 380 300 310 Q 480 230 620 340 Q 780 440 950 360 Q 1040 320 1100 380"
                stroke="rgba(18,178,241,0.3)"
                strokeWidth="1.5"
                strokeDasharray="3 14"
                strokeLinecap="round"
                fill="none"
              />
              <circle cx="160" cy="100" r="4" fill="#12b2f1" opacity="0.6" />
              <circle cx="320" cy="178" r="5" fill="#12b2f1" opacity="0.5" />
              <circle cx="500" cy="238" r="3.5" fill="#12b2f1" opacity="0.55" />
              <circle cx="660" cy="168" r="4.5" fill="#12b2f1" opacity="0.45" />
              <circle cx="840" cy="210" r="4" fill="#12b2f1" opacity="0.5" />
              <circle cx="1020" cy="248" r="3.5" fill="#12b2f1" opacity="0.4" />
              <line
                x1="440"
                y1="272"
                x2="440"
                y2="282"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.45"
              />
              <line
                x1="435"
                y1="277"
                x2="445"
                y2="277"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.45"
              />
              <line
                x1="760"
                y1="136"
                x2="760"
                y2="146"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.4"
              />
              <line
                x1="755"
                y1="141"
                x2="765"
                y2="141"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.4"
              />
            </svg>
          </Box>

          <Grid
            container
            spacing={6}
            alignItems="center"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Grid size={{ xs: 12, lg: 5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {steps.map(({ number, label, description }) => (
                  <Box
                    key={number}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 2.5,
                      borderRadius: 2,
                      border: "1px solid rgba(18,178,241,0.2)",
                      backgroundColor: "white",
                      transition: "0.25s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                        boxShadow: "0 6px 18px rgba(18,178,241,0.12)",
                        borderColor: "#12b2f1",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#12b2f1",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 900,
                        flexShrink: 0,
                      }}
                    >
                      {number}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, mb: 0.5, lineHeight: 1.3 }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(0,0,0,0.6)",
                          fontWeight: 500,
                          lineHeight: 1.6,
                        }}
                      >
                        {description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 7 }}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow:
                    "0 24px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(18,178,241,0.1)",
                  border: "1px solid rgba(18,178,241,0.15)",
                  transform: {
                    xs: "none",
                    lg: "perspective(1200px) rotateY(-3deg) rotateX(1deg)",
                  },
                  transition: "0.4s ease",
                  "&:hover": {
                    transform:
                      "perspective(1200px) rotateY(0deg) rotateX(0deg)",
                    boxShadow:
                      "0 32px 80px rgba(0,0,0,0.14), 0 8px 24px rgba(18,178,241,0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#f1f3f5",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#ff5f57",
                    }}
                  />
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#febc2e",
                    }}
                  />
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#28c840",
                    }}
                  />
                  <Box
                    sx={{
                      ml: 1.5,
                      flex: 1,
                      bgcolor: "white",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.4,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#888",
                        fontFamily: "monospace",
                      }}
                    >
                      kloudscan.com/dashboard
                    </Typography>
                  </Box>
                </Box>

                {/* ✅ Updated to use the imported asset variable */}
                <Box
                  component="img"
                  src={dashboardGraphic}
                  alt="Kloudscan dashboard showing removal requests and data exposure"
                  sx={{
                    width: "100%",
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
